// @ts-strict-ignore
import React, { useEffect, useMemo } from 'react';
import SingleSelect from '~/components/dropdowns/SingleSelect';
import type {
  Attribute,
  AttributeComponent,
  DependentDropdownOptions,
} from '~/flow/types';
import type {
  CurrValue,
  HandleOnChange,
} from './types';

export const ATTRIBUTES_WITH_RHS_DEPENDENT_DROPDOWNS_SET = new Set([
  'alh_latest_eval_outcome',
  'alh_outcome_reason',
  'alh_reason_code',
]);

const ATTRIBUTES_DEPENDENT_ON_JOURNEY_TOKEN_SET = new Set([
  'alh_outcome_reason',
  'alh_reason_code',
]);

const getValueDependentOnByPlaceholder = ({
  attrComponents,
  placeholder: _placeholder,
}): string | number | null => {
  const attrComponent = attrComponents.find(({ placeholder }) => placeholder === _placeholder);
  return attrComponent.value || attrComponent.default || null;
};

export const getValueDependentOn = ({
  attrComponents,
  attrName,
}): string | number | null => {
  if (ATTRIBUTES_DEPENDENT_ON_JOURNEY_TOKEN_SET.has(attrName)) {
    const journeyToken = getValueDependentOnByPlaceholder({
      attrComponents,
      placeholder: '{journey}',
    });
    return journeyToken;
  }

  if (attrName === 'alh_latest_eval_outcome') {
    const workflowToken = getValueDependentOnByPlaceholder({
      attrComponents,
      placeholder: '{workflow}',
    });
    return workflowToken;
  }

  return null;
};

const getDefaultValue = ({ options }): string => {
  const defaultValue = options[0]?.value;

  if (defaultValue === undefined) {
    return '';
  }

  return defaultValue;
};

const RHSDependentDropdown = ({
  currValue,
  handleOnChange,
  leftAttrComponents,
  leftAttrName,
  rightSideDropdownOptions,
}: {
  currValue: CurrValue;
  handleOnChange: HandleOnChange;
  leftAttrComponents: AttributeComponent[];
  leftAttrName: Attribute['attribute_name'];
  rightSideDropdownOptions: DependentDropdownOptions;
}) => {
  const valueDependentOn = getValueDependentOn({
    attrComponents: leftAttrComponents,
    attrName: leftAttrName,
  });

  const options = useMemo(
    () => rightSideDropdownOptions[valueDependentOn] || [],
    [rightSideDropdownOptions, valueDependentOn],
  );

  useEffect(() => {
    if (!options.length && currValue === '') {
      return;
    }
    if (!options.map(({ value }) => value).includes(currValue)) {
      const defaultValue = getDefaultValue({ options });
      handleOnChange({ value: defaultValue });
    }
  }, [
    currValue,
    handleOnChange,
    leftAttrName,
    options,
    valueDependentOn,
  ]);

  return (
    <SingleSelect
      handleOnChange={handleOnChange}
      options={options}
      selectedOptions={currValue}
    />
  );
};

export default RHSDependentDropdown;
